import { isEqual, sortBy } from "lodash-es";

export function searchStoreUrl(searchStore, prefix = "", ignoreKeys = []) {
  const url = new URL(location);
  const searchParams = url.searchParams;

  /*
    url.searchParams cannot be set to a new value. Modify existing value by
    deleting all query params controlled by this searchStore to start fresh for simplicity
  */
  Object.values(searchStore.queryParamMap).forEach((key) => searchParams.delete(prefix + key));

  /*
    Fetch searchStore *state* keys that are modified from their default values
    and translate them to URLSearchParam keys
  */
  const configuredParamKeys = Object.keys(searchStore.configuredFilterKeys).map(
    (k) => searchStore.queryParamMap[k]
  );
  /* All the query params that would be sent for this search to our actual search API */
  const searchStoreQueryParams = searchStore.requestQueryParams;

  configuredParamKeys.forEach((key) => {
    if (ignoreKeys.includes(key)) {
      // ignore
    } else if (!searchStoreQueryParams.has(key)) {
      /*
        Some searchStore state keys are *modified*, but to a nothing value -
        as such, they are not necessarily sent with the search request to fetch records.
        We still need to persist these to the URL bar because
        they are changes from the default, so we denote them as params with empty values
      */
      searchParams.set(prefix + key, "");
    } else {
      searchStoreQueryParams
        .getAll(key)
        .forEach((value) => searchParams.append(prefix + key, value));
    }
  });

  return url;
}

/*
  Subscribes to search store changes to use history.push/replaceState to update the URL.
  Returns a function that can be used to update the searchStore from the URL.
*/
export default (searchStore, prefix = "", ignoreKeys = []) => {
  searchStore.$subscribe((_mutation, _state) => {
    /* These will be unmodified so we can see changes */
    const previousSearchParams = new URL(location).searchParams;
    /* These will be modified to update the url */
    const url = searchStoreUrl(searchStore, prefix, ignoreKeys);
    const searchParams = url.searchParams;

    const changedParams = Object.values(searchStore.queryParamMap).filter(
      (key) =>
        !isEqual(
          sortBy(searchParams.getAll(prefix + key)),
          sortBy(previousSearchParams.getAll(prefix + key))
        )
    );

    if (changedParams.length === 0) {
      return;
    }

    /*
      The store gets updated on every keypress, but we don't want to spam history
      with one character changes.
      If only the query (q) changed, only 'push' to history on new words, otherwise 'replace'
    */
    const oldQuery = previousSearchParams
      .get(prefix + "q")
      ?.toLowerCase()
      ?.trim();
    const newQuery = searchParams
      .get(prefix + "q")
      ?.toLowerCase()
      ?.trim();
    if (isEqual(changedParams, ["q"]) && oldQuery && newQuery) {
      const hasSameWords = oldQuery.split(" ").length == newQuery.split(" ").length;
      const [smaller, bigger] = [oldQuery, newQuery].sort((a, b) => a.length - b.length);
      if (hasSameWords && bigger.includes(smaller)) {
        /* only q updated, no new words, replace history */
        history.replaceState({}, "", url);
        return;
      }
    }

    history.pushState({}, "", url);
  });

  return () => {
    const url = new URL(location);
    const params = new URLSearchParams();
    /* filter out only relevant params and strip prefix */
    Object.values(searchStore.queryParamMap).forEach((key) => {
      if (url.searchParams.has(prefix + key)) {
        url.searchParams.getAll(prefix + key).forEach((value) => params.append(key, value));
      }
    });

    searchStore.setFromSearchParams(params);
  };
};
